import { NestedMenuItem } from 'mui-nested-menu';
import { TaskWrapper, WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import ActionButton from 'src/components/ActionButton';
import {
  Edit as EditIcon,
  ChevronRight as ChevronRightIcon,
  Check as CheckIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
  Description as DescriptionIcon,
  CopyAllRounded as CopyTaskIcon,
  NoteAddOutlined as NoteIcon,
  Link as LinkIcon,
  OpenInBrowser,
  OpenInNew,
} from '@mui/icons-material';
import { Box, Menu, MenuItem, SxProps, Typography } from '@mui/material';
import { TaskStatus, getTaskOwnerByReference, getOwnerDisplayLabel } from './TaskModal';
import useObjectState from 'src/hooks/useObjectState';
import { getValueSetConceptValue } from 'src/careflow/helpers/common';
import { useEffect, useMemo, useState } from 'react';
import { onSuccess } from 'src/@types/crs/case';
import { updateTask } from '../../main/task.api';
import { useSnackbar } from 'notistack';
import SearchTextField from 'src/components/SearchTextField';
import moment from 'moment';
import { getSeverityDueDateData } from 'src/careflow/helpers/getSeverityDueDateData';
import DatePickerMoment from 'src/components/DatePickerMoment';
import { Task } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/Task';
import useTaskContext from '../../common/hook/useTaskContext';
import usePermissionsContext from 'src/careflow/ph-core/admin/permissions/hook/usePermissionsContext';
import { taskHandlerMap, TaskPermissions } from 'src/careflow/helpers/types';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { isValidURL } from 'src/utils/url';
import { useTask } from 'src/careflow/ph-core/fhir-client/fhir-react';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import Iconify from 'src/components/Iconify';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { itsNatTask } from 'src/careflow/inck/nat-task/helpers/task';

export type onResourceAction = (task: WrappedTask) => void;

export interface UrlData {
  url: string | undefined;
  label: string | undefined;
}

export type OnInternalUrlClick = (urlData: UrlData) => void;

export interface TaskActionMenuProps {
  onEditTask: onResourceAction;
  // task: WrappedTask;
  taskExternal?: WrappedTask | null;
  taskId?: string;
  isNestedGrid?: boolean;
  onSuccessfulEdit: onSuccess;
  onTaskOpen: onResourceAction;
  onSearchMemberOpen: onResourceAction;
  onCopyTask: onResourceAction;
  onAddNoteToTask: onResourceAction;
  taskStatuses: TaskStatus[];
  onInternalUrlClick?: OnInternalUrlClick;
  onSubTask?: onResourceAction;
  isLoading?: boolean;
  taskHandlerMap?: taskHandlerMap[];
}

interface TaskActionState {
  status: string | null;
  endDate: moment.Moment | null;
  taskStatusOptions: TaskStatus[];
  openStatus: null | HTMLElement;
  openOwner: null | HTMLElement;
  openDate: null | HTMLElement;
  openTaskUrlResources: null | HTMLElement;
  disabledStatus?: boolean;
}

const TaskActionMenu = ({
  // task,
  taskExternal,
  taskId,
  isNestedGrid,
  onEditTask,
  onSuccessfulEdit,
  onSearchMemberOpen,
  onCopyTask,
  onTaskOpen,
  onAddNoteToTask,
  taskStatuses,
  onInternalUrlClick,
  onSubTask,
  isLoading,
  taskHandlerMap
}: TaskActionMenuProps) => {
  const [verifiedNatTaskBoolean, setVerifiedNatTaskBoolean] = useState(false);

  const [taskRecord, { refresh: refreshTask } ] = useTask(taskId, {
    map: TaskWrapper,
    autofetch: !!taskId,
  });

  useEffect(() => { 
    if(isLoading){
      refreshTask();
    }
  },[isLoading]);

  const task = useMemo(() => {
    if (taskExternal) return taskExternal;
    if (taskRecord) return taskRecord;
    return null;
  }, [taskRecord, taskExternal]);

  const [{ 
    status, 
    endDate, 
    openStatus, 
    openOwner, 
    openDate,
    openTaskUrlResources,
    disabledStatus
  }, updateState] = useObjectState<TaskActionState>({
    status: task?.status ?? null,
    taskStatusOptions: [],
    endDate: null,
    openStatus: null,
    openOwner: null,
    openDate: null,
    openTaskUrlResources: null,
    disabledStatus: false
  });

  const { i18n } = useLocales();
  const { menuTaskHandlers } = useTaskContext();
  const { isAllowedToEdit, notes } = usePermissionsContext<TaskPermissions>() ?? {};
  
  const { componentsData } = useTenantConfigData();
  const taskTypeMappings = componentsData?.task?.taskTypeMappings || {};
  const  { activityDefintionsDisabledStatus = null } = componentsData?.task ?? {};
  
  const getTaskTypeConfig = (taskCode: string) => {
    return taskTypeMappings[taskCode] || null;
  };

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const selectedStatus = getValueSetConceptValue(taskStatuses, task?.status);
    updateState({
      status: selectedStatus?.code ?? null,
      endDate: moment(task?.getDueDate() ?? null).isValid() ? moment(task?.getDueDate()) : null,
      taskStatusOptions: [
        ...taskStatuses,
        ...(selectedStatus && !taskStatuses.find(({ code }) => code === selectedStatus?.code)
          ? [selectedStatus]
          : []),
      ],
    });
    if (task) {
      const verifiedNatTask = itsNatTask(task);
      setVerifiedNatTaskBoolean(verifiedNatTask);

      const disabledStatus = (task?.instantiatesCanonical && task?.isActive())
        ? activityDefintionsDisabledStatus?.includes(task.instantiatesCanonical) 
        : false;
      if(disabledStatus){
        updateState({ disabledStatus: true });
      }
    }
  }, [task, taskStatuses, activityDefintionsDisabledStatus]);

  const taskUrlResources = task?.getURLResources?.() ?? [];

  const handleOpenStatusMenu = (event: React.MouseEvent<HTMLElement>) => {
    updateState({ [event.currentTarget.id]: event.currentTarget })
  };

  return (
    <ActionButton
      renderChildren={(isMenuOpen, setIsMenuOpen) => (
        <Box>
          {task ? (
            <>
              <div tabIndex={1}>
                <MenuItem
                  disabled={verifiedNatTaskBoolean
                    ? !checkAclValidation({ acls: [crsAcls.NAT.TASK.EDIT] }) 
                    : !isAllowedToEdit
                  }
                  sx={nestedMenuItemStyles}
                  onClick={() => {
                    setIsMenuOpen(false);
                    onEditTask(task);
                  }}
                >
                  <Box display={'flex'}>
                    <EditIcon htmlColor="#00ab55" />
                    <Typography textAlign={'left'} px={'8px'}>
                      Edit
                    </Typography>
                  </Box>
                </MenuItem>
              </div>
              <div tabIndex={-1}>
                <MenuItem
                  id="openStatus"
                  key="openStatus"
                  disabled={
                    verifiedNatTaskBoolean 
                    ? !checkAclValidation({ acls: [crsAcls.NAT.TASK.EDIT] }) 
                    : disabledStatus 
                    ? disabledStatus :
                    false
                  }
                  onClick={handleOpenStatusMenu}
                  sx={{ px: '24px', display: 'flex', justifyContent: 'space-between'}}
                >
                  <Box display={'flex'}>
                    <AccessTimeIcon/>
                    <Typography textAlign={'left'} px={'8px'}>
                      Status
                    </Typography>
                  </Box>
                  <ChevronRightIcon/>
                </MenuItem>
                <Menu
                  anchorEl={openStatus}
                  open={Boolean(openStatus)}
                  onClose={() => updateState({ openStatus : null })}
                  anchorOrigin={{ vertical: "center", horizontal: "left" }}
                  transformOrigin={{ vertical: "center", horizontal: "right" }}
                >
                  {taskStatuses.map((taskStatus) => (
                    <MenuItem
                      key={taskStatus.code}
                      onClick={async () => {
                        if (task?.status) {
                          task.status = taskStatus.code as Task["status"];
                        }
                        updateState({ status: taskStatus.code });
                        const response = await updateTask(
                          { status: taskStatus.code },
                          task?.id as string
                        );
                        if (!response) {
                          enqueueSnackbar("Unable to update Task Status. Please Try Again", {
                            variant: "error",
                          });
                          return;
                        }
                        onSuccessfulEdit(response);
                        updateState({ openStatus : null })
                      }}
                    >
                      {taskStatus.code === status && <CheckIcon style={{ marginRight: 8 }} />}
                      {taskStatus.display}
                    </MenuItem>
                  ))}
                </Menu>
              </div>
              <div tabIndex={1}>
                <MenuItem
                  id="openOwner"
                  key="openOwner"
                  disabled={verifiedNatTaskBoolean ? 
                    !checkAclValidation({ acls: [crsAcls.NAT.TASK.EDIT] }) : false
                  }
                  onClick={handleOpenStatusMenu}
                  sx={{ px: '24px', display: 'flex', justifyContent: 'space-between'}}
                >
                  <Box display={'flex'}>
                    <PersonIcon/>
                    <Typography textAlign={'left'} px={'8px'}>
                      Owner
                    </Typography>
                  </Box>
                  <ChevronRightIcon/>
                </MenuItem>
                <Menu
                  anchorEl={openOwner}
                  open={Boolean(openOwner)}
                  onClose={() => updateState({ openOwner : null })}
                  anchorOrigin={{ vertical: "center", horizontal: "left" }}
                  transformOrigin={{ vertical: "center", horizontal: "right" }}
                >
                  <NestedMenuItem
                    sx={nestedMenuItemStyles}
                    leftIcon={
                      getOwnerDisplayLabel(getTaskOwnerByReference(task?.owner)) ? (
                        <CheckIcon />
                      ) : null
                    }
                    rightIcon={null}
                    label={getOwnerDisplayLabel(getTaskOwnerByReference(task?.owner))}
                    parentMenuOpen={isMenuOpen}
                  />
                  <MenuItem>
                    <SearchTextField
                      fullWidth
                      onFocus={() => {
                        onSearchMemberOpen(task);
                        setIsMenuOpen(false);
                      }}
                    />
                  </MenuItem>
                </Menu>
              </div>
              <div tabIndex={1}>
                <MenuItem
                  id="openDate"
                  key="openDate"
                  disabled={verifiedNatTaskBoolean ? 
                    !checkAclValidation({ acls: [crsAcls.NAT.TASK.EDIT] }) : false
                  }
                  onClick={handleOpenStatusMenu}
                  sx={{ px: '24px', display: 'flex', justifyContent: 'space-between'}}
                >
                  <Box display={'flex'}>
                    <CalendarTodayIcon/>
                    <Typography textAlign={'left'} px={'8px'}>
                    {i18n('tasks.dueDate', 'crs')}
                    </Typography>
                  </Box>
                  <ChevronRightIcon/>
                </MenuItem>
                <Menu
                  anchorEl={openDate}
                  open={Boolean(openDate)}
                  onClose={() => updateState({ openDate : null })}
                  anchorOrigin={{ vertical: "center", horizontal: "left" }}
                  transformOrigin={{ vertical: "center", horizontal: "right" }}
                >
                  <NestedMenuItem
                    sx={nestedMenuItemStyles}
                    leftIcon={<CheckIcon />}
                    rightIcon={null}
                    label={
                      getSeverityDueDateData(moment(endDate?.toISOString() ?? null))
                        ?.formattedDueDate ?? ''
                    }
                    parentMenuOpen={isMenuOpen}
                  />
                  <MenuItem>
                    <DatePickerMoment
                      value={endDate}
                      label={i18n('tasks.dueDate', 'crs')}
                      onChange={async (dueDate) => {
                        updateState({ endDate: dueDate });
                        if (!dueDate?.isValid?.()) return;
                        if (!task?.extension) task.extension = [];
                        let parentExtension = task.extension.find(
                          (ext) => ext?.url === 'https://terminology.patientholistics.app/fhir/StructureDefinition/ph-task-detail'
                        );
                        if (!parentExtension) {
                          parentExtension = {
                            url: 'https://terminology.patientholistics.app/fhir/StructureDefinition/ph-task-detail',
                            extension: []
                          };
                          task.extension.push(parentExtension);
                        }
                        if (!parentExtension.extension) parentExtension.extension = [];
                        let requestedPeriodExt = parentExtension.extension.find(
                          (ext) => ext?.url === 'requestedPeriod'
                        );
                        if (!requestedPeriodExt) {
                          requestedPeriodExt = { url: 'requestedPeriod', valuePeriod: { end: dueDate?.toISOString() } };
                          parentExtension.extension.push(requestedPeriodExt);
                        } else {
                          requestedPeriodExt.valuePeriod = {
                            ...requestedPeriodExt.valuePeriod,
                            end: dueDate?.toISOString()
                          };
                        }
                        const response = await updateTask(
                          { requestedEnd: dueDate.toISOString() },
                          task?.id as string
                        );
                        if (!response) {
                          enqueueSnackbar('Unable to update Task Status. Please Try Again', {
                            variant: 'error',
                          });
                          return;
                        }
                        setIsMenuOpen(false);
                        onSuccessfulEdit(response);
                      }}
                    />
                  </MenuItem>
                </Menu>
              </div>
              <div tabIndex={1}>
                <MenuItem
                  sx={nestedMenuItemStyles}
                  onClick={() => {
                    setIsMenuOpen(false);
                    onCopyTask(task);
                  }}
                >
                  <Box display={'flex'}>
                    <CopyTaskIcon />
                    <Typography textAlign={'left'} px={'8px'}>
                      Copy
                    </Typography>
                  </Box>
                </MenuItem>
              </div>
              <div tabIndex={1}>
                <MenuItem
                  sx={nestedMenuItemStyles}
                  onClick={() => {
                    setIsMenuOpen(false);
                    onTaskOpen(task);
                  }}
                >
                  <Box display={'flex'}>
                    <DescriptionIcon />
                    <Typography textAlign={'left'} px={'8px'}>
                      Detail
                    </Typography>
                  </Box>
                </MenuItem>
              </div>
              <div tabIndex={1}>
                <MenuItem
                  disabled={ verifiedNatTaskBoolean 
                    ? !checkAclValidation({ acls: [crsAcls.NAT.TASK.NOTES.VIEW] }) 
                    : !notes?.isAllowedToView
                  }
                  sx={nestedMenuItemStyles}
                  onClick={() => {
                    setIsMenuOpen(false);
                    onAddNoteToTask(task);
                  }}
                >
                  <Box display={'flex'}>
                    <NoteIcon />
                    <Typography textAlign={'left'} px={'8px'}>
                      Notes
                    </Typography>
                  </Box>
                </MenuItem>
              </div>
              {menuTaskHandlers?.map?.(
                ({ icon, labelString, taskType, handler, conditionToShow }, index) => {
                  const getHandler = taskHandlerMap?.find((e) => e?.taskType === taskType);
                  const label = labelString(i18n);
                  if (!conditionToShow(task, getHandler?.permission)) return null;
                  return (
                    <MenuItem
                      key={`task-menu-item-${index}`}
                      sx={nestedMenuItemStyles}
                      onClick={() => {
                        setIsMenuOpen(false);
                        handler?.(task, getHandler?.handler);
                      }}
                    >
                      <Box display={'flex'}>
                        {icon}
                        <Typography textAlign={'left'} px={'8px'}>
                          {label}
                        </Typography>
                      </Box>
                    </MenuItem>
                  );
                }
              )}
              {!!taskUrlResources.length && (
                <div tabIndex={-1}>
                  <MenuItem
                    id="openTaskUrlResources"
                    key="openTaskUrlResources"
                    onClick={handleOpenStatusMenu}
                    sx={{ px: '24px', display: 'flex', justifyContent: 'space-between'}}
                  >
                    <Box display={'flex'}>
                    <LinkIcon />
                      <Typography textAlign={'left'} px={'8px'}>
                        {i18n('tasks.resources', 'crs')}
                      </Typography>
                    </Box>
                    <ChevronRightIcon/>
                  </MenuItem>
                  <Menu
                    anchorEl={openTaskUrlResources}
                    open={Boolean(openTaskUrlResources)}
                    onClose={() => updateState({ openTaskUrlResources : null })}
                    anchorOrigin={{ vertical: "center", horizontal: "left" }}
                    transformOrigin={{ vertical: "center", horizontal: "right" }}
                  >
                    {taskUrlResources.map((input, index) => {
                      const isInternal = input?.type?.coding?.[0]?.code?.includes?.('internal');
                      const label = input?.type?.text;
                      return (
                        <NestedMenuItem
                          key={`url-resource-${input?.type?.coding?.[0]?.code}-${index}`}
                          onClick={() => {
                            const url = input?.valueUrl;

                            if (!isValidURL(url ?? '')) return;
                            if (!isInternal) return window.open(url, '_blank');

                            onInternalUrlClick?.({ url, label });
                          }}
                          sx={nestedMenuItemStyles}
                          leftIcon={isInternal ? <OpenInBrowser /> : <OpenInNew />}
                          rightIcon={null}
                          label={label}
                          parentMenuOpen={isMenuOpen}
                        />
                      );
                    })}
                  </Menu>
                </div>
              )}
              {!isNestedGrid && onSubTask && !verifiedNatTaskBoolean && (
                <MenuItem
                  sx={nestedMenuItemStyles}
                  onClick={() => {
                    setIsMenuOpen(false);
                    onSubTask?.(task);
                  }}
                >
                  <Box display={'flex'}>
                    <Iconify icon="mdi:subtasks" height={20} width={20}/>
                    <Typography textAlign={'left'} px={'8px'}>
                      Add Sub Task
                    </Typography>
                  </Box>
                </MenuItem>
              )}
            </>
          ) : null}
        </Box>
      )}
    />
  );
};

const nestedMenuItemStyles: SxProps = {
  paddingX: 3,
};

export default TaskActionMenu;
